import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { api } from "../lib/api";
import { logError, toUserMessage } from "../lib/errors";
import { useCurrentCharacterName } from "../hooks/useCurrentCharacterName";
import { buildGroupTalkPreviewMembers } from "../lib/groupTalk";
import {
  clearGroupTalkDraft,
  loadGroupTalkDraft,
  saveGroupTalkActiveSessionSnapshot,
  type GroupTalkDraft,
} from "../lib/groupTalkSessionStorage";
import { buildCreateGroupTalkSessionParams, normalizeGroupTalkSessionView } from "../lib/groupTalkView";

type PreviewState = {
  draft?: GroupTalkDraft;
};

export default function GroupTalkPreview() {
  const characterName = useCurrentCharacterName();
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = (typeof location.state === "object" && location.state ? location.state : {}) as PreviewState;
  const draft = routeState.draft ?? loadGroupTalkDraft();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const previewMembers = useMemo(() => {
    if (!draft) return [];
    return buildGroupTalkPreviewMembers(draft.participants, characterName);
  }, [characterName, draft]);

  useEffect(() => {
    if (!draft) {
      navigate("/talk/group", {
        replace: true,
        state: { errorMessage: "会議設定が見つからなかったため、メンバー選択からやり直してください。" },
      });
    }
  }, [draft, navigate]);

  if (!draft) return null;

  const startSession = async () => {
    if (creating) return;
    setCreating(true);
    setError("");

    try {
      const response = await api.createGroupTalkSession(buildCreateGroupTalkSessionParams({ draft }));
      const normalizedSession = normalizeGroupTalkSessionView(response, characterName);
      saveGroupTalkActiveSessionSnapshot({
        sessionId: normalizedSession.sessionId,
        draft,
        session: normalizedSession,
        turns: [],
      });
      clearGroupTalkDraft();
      navigate(`/talk/group/sessions/${encodeURIComponent(normalizedSession.sessionId)}`, {
        replace: true,
      });
    } catch (e) {
      logError(e);
      setError(toUserMessage(e));
    } finally {
      setCreating(false);
    }
  };

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-3xl">
        <header className="mb-6 flex items-center justify-between">
          <Link to="/talk/group" className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
            ← 戻る
          </Link>
          <p className="text-sm font-bold tracking-tight text-[#7C5A4E]">開始前の確認</p>
          <Link to="/home" className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
            WizMate
          </Link>
        </header>

        <section className="rounded-[2.4rem] border border-white/80 bg-white/90 p-6 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-7">
          <span className="inline-flex rounded-full bg-[#FFF3E9] px-3 py-1 text-[11px] font-black text-[#A46D4C]">確認中</span>
          <h1 className="mt-4 text-3xl font-black tracking-tight text-[#7C5A4E]">{draft.title}</h1>

          <div className="mt-8 rounded-[2rem] border border-[#EADBC8] bg-[#FFFBF8] p-5">
            <p className="text-sm font-black text-[#5D4D46]">このメンバーで始めます</p>
            <div className="mt-4 space-y-3">
              {previewMembers.map((member) => (
                <div
                  key={member.participantId}
                  className={`rounded-[1.4rem] border px-4 py-3 ${
                    member.roleLabel === "ファシリテータ"
                      ? "border-[#E8CDBE] bg-[#FFF1E8]"
                      : "border-[#D9C5B9] bg-white"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 pr-2">
                      <p className="text-sm font-black text-[#5D4D46]">{member.label}</p>
                      <p className="mt-1 text-sm text-[#6B5851]">{member.roleLabel}</p>
                    </div>
                    <span className="inline-flex min-w-[7.5rem] shrink-0 justify-center whitespace-nowrap rounded-full bg-[#F7F1EA] px-3 py-1 text-[11px] font-black text-[#8F7468]">
                      {member.stanceLabel ?? "スタンスなし"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {error ? (
            <p role="alert" className="mt-5 text-sm font-bold text-[#C06D5A]">
              {error}
            </p>
          ) : null}

          <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Link
              to="/talk/group"
              state={{ draft }}
              className="rounded-full border border-[#EADBC8] bg-white px-5 py-3 text-center text-sm font-bold text-[#5D4D46]"
            >
              メンバーを調整する
            </Link>
            <button
              type="button"
              onClick={() => void startSession()}
              disabled={creating}
              className="rounded-full bg-[#5D4D46] px-6 py-3 text-sm font-black text-white transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {creating ? "会議を開始中..." : "会議を開始する"}
            </button>
          </div>
        </section>
      </div>
    </main>
  );
}
